import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _noop from 'lodash/noop';
import _size from 'lodash/size';
import PropTypes from 'prop-types';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import mDevice from 'models/device';
// Custom
import {
  openDialog, sendCmdDevice, updateInstallationDevice, deleteInstallationDevice, deleteInstallationDeviceSlave,
} from 'actions';
import { devicesShape, deleteShape } from 'constants/PropTypesShapes';
import { configurationDevicesAddPath } from 'installations/navigation';
import SubListTemplate from 'components/templates/SubListTemplate';
import withStateReinit from 'hocs/WithStateReinit';
import withConfirmDialog from 'hocs/WithConfirmDialog';
import withPermissions from 'hocs/WithPermissions';
import { AddTextButton } from 'components/buttons/TextButtons';
import withEntitiesInformation from 'installations/assets/hocs/WithEntitiesInformation';
import DeviceRow from 'installations/assets/configuration/DeviceRow';
import DeviceRowDetails from 'installations/assets/configuration/DeviceRowDetails';

class Devices extends React.Component {
  constructor(props) {
    super(props);

    this.handleSync = this.handleSync.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleDeleteSlave = this.handleDeleteSlave.bind(this);
    this.doGetRowComp = this.doGetRowComp.bind(this);
    this.doGetRowCompDetails = this.doGetRowCompDetails.bind(this);
  }

  componentDidUpdate(prevProps) {
    const {
      updated, deleted, configUpdated, deviceSlaveDelete, onUpdate,
    } = this.props;
    if (
      (!prevProps.updated && updated)
      || (!prevProps.deleted && deleted)
      || (!prevProps.configUpdated && configUpdated)
      || (!prevProps.deviceSlaveDelete.deleted && deviceSlaveDelete.deleted)
    ) {
      onUpdate();
    }
  }

  // Handlers
  handleSync(device) {
    const { handleDisplayConfirm, handleSendSyncCmdDevice } = this.props;
    const cmd = mDevice.commands.sync;
    handleDisplayConfirm(
      { id: 'confirm.sendCmdDevice', values: { cmd } },
      () => handleSendSyncCmdDevice(device.id, mDevice.minimize(device), cmd),
      { confirmLabel: 'send' },
    );
  }

  handleEdit(device) {
    const { handleOpenDialog } = this.props;
    handleOpenDialog({
      title: {
        id: 'title.editDevice',
      },
      actions: [
        { type: 'cancel' },
        { type: 'validate' },
      ],
      component: {
        type: 'INSTALLATION_DEVICE_EDIT',
        props: {
          device,
          onValidate: (data) => this.handleValidateEdit(device.id, data),
        },
      },
    });
  }

  handleValidateEdit(idDevice, { name, disabled }) {
    const { id, handleUpdateDevice } = this.props;
    handleUpdateDevice(id, idDevice, mDevice.serializeInstallationDevice({ name, disabled }));
  }

  handleDelete(device) {
    const { id, handleDisplayConfirm, handleDeleteDevice } = this.props;
    handleDisplayConfirm(
      { id: 'confirm.deleteDeviceInstallation', values: { deviceName: mDevice.installationName(device) } },
      () => handleDeleteDevice(id, device.id),
    );
  }

  handleDeleteSlave(device, deviceSlave) {
    const { id, handleDisplayConfirm, handleDeleteDeviceSlave } = this.props;
    handleDisplayConfirm(
      { id: 'confirm.deleteDeviceSlaveInstallation', values: { deviceName: mDevice.installationName(device), deviceSlaveName: deviceSlave.name } },
      () => handleDeleteDeviceSlave(id, device.id, deviceSlave.id),
    );
  }

  // Private
  doGetRowComp(props) {
    const { id } = this.props;
    return (
      <DeviceRow
        {...props}
        id={id}
        onSync={this.handleSync}
      />
    );
  }

  doGetRowCompDetails(props) {
    const { id } = this.props;

    return (
      <DeviceRowDetails
        {...props}
        id={id}
        onEdit={this.handleEdit}
        onDelete={this.handleDelete}
        onDeleteSlave={this.handleDeleteSlave}
      />
    );
  }

  render() {
    const {
      id, devices, hasPermission,
    } = this.props;
    const sortedDevices = mDevice.sortElements(devices.elements, ['installation.device.name']);

    // For the sub list, create the good structure
    const devicesList = {
      loading: devices.loading,
      elements: {
        data: sortedDevices,
        totalEntries: _size(sortedDevices),
      },
    };

    // Permissions
    const { pInstallation: { configWrite }, pDevice } = permissions;

    return (
      <SubListTemplate
        title={<LocalizedMessage id="title.devices" />}
        actionButtons={[
          <OnlyWith
            key="bAdd"
            permissions={{
              needed: `${configWrite}&&${pDevice.read}`,
              permissionsOrContext: 'installationPermissions',
            }}
          >
            <AddTextButton primary path={configurationDevicesAddPath(id)} />
          </OnlyWith>,
        ]}
        name={mDevice.name}
        listFeatures={mDevice.listFeaturesInstallation}
        withQueryParams={false}
        withDetails
        withActions={hasPermission({ needed: pDevice.read, permissionsOrContext: 'userPermissions' })}
        nbActions={2}
        elementsData={{
          loading: devicesList.loading,
          elements: devicesList.elements,
          handleGetList: _noop,
        }}
        withPagination={false}
        withVirtualization={false}
        rowComp={this.doGetRowComp}
        rowCompDetails={this.doGetRowCompDetails}
      />
    );
  }
}

Devices.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  devices: devicesShape.isRequired,
  updated: PropTypes.bool.isRequired,
  deleted: PropTypes.bool.isRequired,
  configUpdated: PropTypes.bool.isRequired,
  deviceSlaveDelete: deleteShape.isRequired,
  onUpdate: PropTypes.func.isRequired,
  hasPermission: PropTypes.func.isRequired,
  handleDisplayConfirm: PropTypes.func.isRequired,
  handleOpenDialog: PropTypes.func.isRequired,
  handleSendSyncCmdDevice: PropTypes.func.isRequired,
  handleUpdateDevice: PropTypes.func.isRequired,
  handleDeleteDevice: PropTypes.func.isRequired,
  handleDeleteDeviceSlave: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  devices: state.installationDevice.list,
  updated: state.installationDevice.update.updated,
  deleted: state.installationDevice.delete.deleted,
  configUpdated: state.deviceConfig.update.updated,
  deviceSlaveDelete: state.installationDeviceSlave.delete,
});

const mapDispatchToProps = (dispatch) => ({
  handleOpenDialog: (props) => {
    dispatch(openDialog(props));
  },
  handleSendSyncCmdDevice: (id, device) => {
    dispatch(sendCmdDevice(id, device, 'sync'));
  },
  handleUpdateDevice: (id, idDevice, data) => {
    dispatch(updateInstallationDevice(id, idDevice, data));
  },
  handleDeleteDevice: (id, idDevice) => {
    dispatch(deleteInstallationDevice(id, idDevice));
  },
  handleDeleteDeviceSlave: (id, idDevice, idSlave) => {
    dispatch(deleteInstallationDeviceSlave(id, idDevice, idSlave));
  },
});

module.exports = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withStateReinit(['listInstallationDevice']),
  withConfirmDialog,
  withPermissions,
  withEntitiesInformation,
)(Devices);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/routes/show/routes/configuration/routes/index/components/InstallationConfigurationDevices.js